import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PaymentServices from "../../services/PaymentServices";
import "./payment.css";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
// Removed AuthServices import - preview endpoint handles promo code validation

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const PaymentPage = ({ amount, totalAmount, installmentAmount, reemcode, onSuccess, pageType }) => {
  const [form, setForm] = useState({
    cardNumber: "",
    expiry: "",
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
    phone: "",
    payPalId: "", // Fixed: Correct field name
  });
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [reemCodeInput, setReemCodeInput] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState(reemcode || null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [planType, setPlanType] = useState("annual");
  const [recurringConsent, setRecurringConsent] = useState(false);
  const [messageType, setMessageType] = useState("error");

  // Base amount from props - this is what we send to backend (before discount)
  const baseAmount = Number(totalAmount) || Number(amount) || 0;
  
  // Current promo code (from URL or applied via button)
  const currentPromoCode = appliedPromoCode || null;

  // Fetch preview when baseAmount, planType, or applied promo code changes
  // Do NOT auto-fetch when user types - only when they click "Apply" button
  useEffect(() => {
    if (!baseAmount || baseAmount <= 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      
      try {
        console.log("Fetching preview:", { baseAmount, currentPromoCode, planType });
        const previewResponse = await PaymentServices.previewPayment(
          baseAmount,
          currentPromoCode,
          planType,
          !!reemcode // isFromUrl: true if promo code came from URL
        );
        console.log("Preview response received:", previewResponse);
        setPreview(previewResponse);
        setPreviewError(null);
      } catch (error) {
        // If preview fails, keep previous preview data but set error state
        // Don't clear preview - this allows user to see previous calculations
        // Only clear preview if this is the initial load (no previous preview exists)
        if (!preview) {
          setPreview(null);
        }
        const errorMessage = error.message || "Failed to load payment preview";
        setPreviewError(errorMessage);
        console.error("Preview failed:", errorMessage, error);
        // Don't throw - just set error state so UI can display it
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview().catch((error) => {
      // Catch any uncaught errors to prevent promise rejection warnings
      console.error("Uncaught preview error:", error);
      setPreviewError(error.message || "Failed to load payment preview");
      setPreviewLoading(false);
    });
  }, [baseAmount, currentPromoCode, planType, reemcode]);

  // Handle promo code apply button click
  const handleApplyPromoCode = async () => {
    if (!reemCodeInput.trim()) {
      setMessage("Please enter a promo code");
      setMessageType("error");
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setMessage("");
    
    try {
      console.log("Applying promo code:", reemCodeInput.trim());
      const previewResponse = await PaymentServices.previewPayment(
        baseAmount,
        reemCodeInput.trim(),
        planType,
        false // Not from URL
      );
      console.log("Promo code preview response:", previewResponse);
      setPreview(previewResponse);
      setAppliedPromoCode(reemCodeInput.trim());
      setPreviewError(null);
      setMessage("Promo code applied successfully");
      setMessageType("success");
    } catch (error) {
      // Don't clear preview on error - keep previous values so user can see what was calculated
      // Only set error state for display purposes
      const errorMessage = error.message || "Invalid promo code";
      setPreviewError(errorMessage);
      setMessage(errorMessage);
      setMessageType("error");
      console.error("Failed to apply promo code:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Display values from preview - use preview if available, otherwise fall back to base amounts
  // Only show 0 if there's an error AND no preview data exists
  const displayTotalAmount = preview?.totalAmount ?? (previewError ? 0 : baseAmount);
  const displayDiscountPercent = preview?.discountPercent || 0;
  const displayBaseAmount = preview?.baseAmount ?? (previewError ? 0 : baseAmount);
  
  // "annual" is what checkout sends now. "lump_sum" is the same plan under its
  // original name and is still the stored planType on every customer who bought
  // before the change, so anything that READS a plan has to accept both - only
  // what we SEND was updated.
  const isAnnualPlan = (t) => t === "annual" || t === "lump_sum";

  // "monthly" is what checkout sends now. "installment" is the same plan under
  // its previous name - it billed twelve times and stopped, where monthly renews
  // until cancelled - and is still the stored planType on customers who bought
  // before the change, so anything READING a plan accepts both.
  const isMonthlyPlan = (t) => t === "monthly" || t === "installment";

  // Installment breakdown - ONLY use preview values, NO fallback
  // Present for both monthly-billed plans; absent for the annual one.
  const installmentBreakdown = isMonthlyPlan(planType) ? preview?.installmentBreakdown : null;
  
  // ONLY use backend-provided values from preview
  // If installmentBreakdown exists, use its values
  // If it doesn't exist but preview exists (for lump_sum), show 0 for installments
  // If previewError is set AND no preview exists, show 0
  const displayFirstPayment = isMonthlyPlan(planType) && installmentBreakdown
    ? (installmentBreakdown.firstPayment ?? 0)
    : 0;
  const displayMonthlyPayment = isMonthlyPlan(planType) && installmentBreakdown
    ? (installmentBreakdown.monthlyPayment ?? 0)
    : 0;
  const displayRemainingPayments = installmentBreakdown?.remainingPayments ?? 11;
  const displayInstallmentsTotal = installmentBreakdown?.installmentsTotal ?? 12;

  // Both installment and pay-in-full set up recurring billing (installment ARB / annual
  // auto-renew), so both require a saved card (no PayPal) and explicit recurring consent.
  const requiresCardPayment = isMonthlyPlan(planType) || isAnnualPlan(planType);
  const originalAmountDisplay = formatter.format(displayBaseAmount || 0);
  const lumpSumDisplay = formatter.format(displayTotalAmount || 0);
  const firstInstallmentDisplay = formatter.format(displayFirstPayment || 0);
  const recurringInstallmentDisplay = formatter.format(displayMonthlyPayment || 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    setMessage("");
    setMessageType("error");

    if (name === "cardNumber") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 16);
    } else if (name === "expiry") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 4);
      if (sanitizedValue.length >= 2) {
        sanitizedValue =
          sanitizedValue.slice(0, 2) +
          (sanitizedValue.length > 2 ? "-" + sanitizedValue.slice(2) : "");
      }
    } else if (name === "cardCode") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 4);
    }

    setForm({ ...form, [name]: sanitizedValue });

    if (
      (name === "cardNumber" && /^[0-9]{16}$/.test(sanitizedValue)) ||
      (name === "expiry" && /^([0-9]{2})\-([0-9]{2})$/.test(sanitizedValue)) ||
      (name === "cardCode" && /^[0-9]{3,4}$/.test(sanitizedValue))
    ) {
      setMessage("");
    }
  };
  const handlePlanChange = (nextPlan) => {
    setPlanType(nextPlan);
    setMessage("");
    setMessageType("error");
    if (isMonthlyPlan(nextPlan) || isAnnualPlan(nextPlan)) {
      // Both set up recurring billing -> force a saved card and require fresh consent.
      setPaymentMethod("card");
      setRecurringConsent(false);
    }
  };
  const validateCardDetails = () => {
    const { cardNumber, expiry, cardCode } = form;

    const isValidCardNumber = (number) => {
      let sum = 0;
      let shouldDouble = false;
      for (let i = number.length - 1; i >= 0; i--) {
        let digit = parseInt(number[i], 10);
        if (shouldDouble) {
          digit *= 2;
          if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
      }
      return sum % 10 === 0;
    };

    if (!/^[0-9]{13,19}$/.test(cardNumber) || !isValidCardNumber(cardNumber)) {
      setMessage("Invalid card number");
      setMessageType("error");
      return false;
    }

    if (!/^([0-9]{2})-([0-9]{2})$/.test(expiry)) {
      setMessage("Invalid expiry date format (MM-YY)");
      setMessageType("error");
      return false;
    }

    const [expMonth, expYearShort] = expiry.split("-").map(Number);
    const currentYearFull = new Date().getFullYear();
    const currentCentury = Math.floor(currentYearFull / 100) * 100;
    const expYear =
      expYearShort +
      (expYearShort < currentYearFull % 100
        ? currentCentury + 100
        : currentCentury);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (
      expYear < currentYear ||
      (expYear === currentYear && expMonth < currentMonth)
    ) {
      setMessage("Card is expired");
      setMessageType("error");
      return false;
    }

    if (!/^[0-9]{3,4}$/.test(cardCode)) {
      setMessage("Invalid CVV (3-4 digits)");
      setMessageType("error");
      return false;
    }

    setMessage("");
    return true;
  };

  function formatMonthYear(input) {
    let [month, year] = input.split("-");
    return `20${year}-${month}`; // Assuming years are in the 2000s
  }

  const handleSubmit = async () => {
    setLoading(true);
    setMessage("");
    setMessageType("error");
    
    // Block submission if preview failed - preview is required
    if (previewError) {
      setLoading(false);
      setMessage("Cannot proceed: Payment preview failed. Please refresh the page or contact support.");
      setMessageType("error");
      return;
    }
    
    if (requiresCardPayment && paymentMethod !== "card") {
      setLoading(false);
      setMessage("Installment plans require a credit or debit card.");
      return;
    }
    if (requiresCardPayment && !recurringConsent) {
      setLoading(false);
      setMessage("Please acknowledge the recurring billing terms before continuing.");
      return;
    }
    if (paymentMethod === "card" && !validateCardDetails()) {
      setLoading(false);
      return;
    }
    try {
      let formattedValue = form.expiry;
      if (paymentMethod === "card") {
        formattedValue = formatMonthYear(form.expiry);
      }
      const paymentDetails =
        paymentMethod === "card"
          ? {
            cardNumber: form.cardNumber,
            expirationDate: formattedValue,
            cardCode: form.cardCode || "123",
          }
          : {
            payPalId: form.payPalId,
          };

      // IMPORTANT: Send BASE amount (before discount) to backend
      // Backend will calculate discount and installment amounts
      
      // Validation: Warn if baseAmount seems suspiciously low (likely a data error)
      if (baseAmount > 0 && baseAmount < 1) {
        console.error("⚠️ WARNING: baseAmount is suspiciously low:", baseAmount);
        console.error("This might indicate a data error. Expected base amount should be the original price (e.g., $9000), not a discounted amount.");
        setMessage("Error: Payment amount appears to be incorrect. Please contact support.");
        setMessageType("error");
        setLoading(false);
        return;
      }
      
      const requestBody = {
        firstName: form.firstName,
        lastName: form.lastName,
        address: form.address,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        country: form.country,
        phone: form.phone,
        amount: baseAmount, // ✅ BASE amount (not discounted)
        reemcode: currentPromoCode || null, // Promo code separately
        planType: planType,
        paymentMethod,
        paymentDetails,
        type: pageType,
        isFromUrl: !!reemcode,
      };

      console.log("Payment requestBody:", requestBody);
      console.log("⚠️ VERIFY: baseAmount should be the original price BEFORE discount (e.g., $9000), not the discounted amount");

      const response = await PaymentServices.payNow(requestBody);
      console.log("Payment response:", response);

      if (response.success) {
        const successText =
          isMonthlyPlan(planType)
            // Phrased the same way for both plans. Neither promises a fixed
            // number of payments any more, because neither has one.
            ? `Subscription activated. Charged today: ${firstInstallmentDisplay}. Then ${recurringInstallmentDisplay} each month until you cancel.${response.subscriptionId ? ` Subscription ID: ${response.subscriptionId}.` : ""}`
            : `Subscription activated. Charged today: ${lumpSumDisplay}. Then ${lumpSumDisplay} each year until you cancel.${response.subscriptionId ? ` Subscription ID: ${response.subscriptionId}.` : ""}`;
        Swal.fire({
          title: "Success!",
          text: successText,
          icon: "success",
          confirmButtonText: "OK",
        });
        setMessage(successText);
        setMessageType("success");
        
        // Clear payment flags after successful payment
        localStorage.removeItem("needsPayment");
        localStorage.removeItem("paymentDetails");
        
        if (typeof onSuccess === "function") {
          onSuccess(response, planType);
        }
        // document.querySelector('.validation-message').style.color = 'green';

        // ✅ Ensure role is correctly parsed from localStorage
        const userrole = Number(localStorage.getItem("userrole")); // Convert string to number
        console.log("Parsed User Role:", userrole);

        // ✅ Add console log before navigating
        console.log("Navigating to:", userrole);

        // ✅ Call `navigate` immediately
        switch (userrole) {
          case 1:
            console.log("Redirecting to /main-dashboard");
            navigate("/main-dashboard");
            break;
          case 2:
            console.log("Redirecting to /main-dashboard");
            navigate("/main-dashboard");
            break;
          case 3:
            console.log("Redirecting to /main-dashboard");
            navigate("/main-dashboard");
            break;
          case "admin":
            console.log("Redirecting to /admin/admin-dashboard");
            navigate("/admin/admin-dashboard");
            break;
          default:
            console.log("Redirecting to /dashboard");
            navigate("/dashboard");
            break;
        }
      } else {
        setMessage(response.message || "Payment failed. Please try again.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Payment Error:", error.response?.data || error.message);
      let errorMessage = error.response?.data?.error || error.message || "Payment failed. Please try again.";
      
      // Provide user-friendly error messages for common AVS and address errors
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('avs') || errorLower.includes('address verification')) {
        errorMessage = "The billing address you entered doesn't match the address on file for this card. Please double-check your address, city, state, and zip code, and make sure they match exactly what your bank has on file. If you're unsure, please contact your bank to verify your billing address.";
      } else if (errorLower.includes('address') && errorLower.includes('match')) {
        errorMessage = "The billing address doesn't match your card's billing address. Please verify that your address, city, state, and zip code are correct and match what your bank has on file.";
      } else if (errorLower.includes('declined')) {
        errorMessage = "Your card was declined. This could be due to insufficient funds, incorrect card details, or your bank blocking the transaction. Please check your card details or contact your bank for assistance.";
      } else if (errorLower.includes('invalid') && errorLower.includes('card')) {
        errorMessage = "The card information you entered appears to be invalid. Please check your card number, expiration date, and CVV code, then try again.";
      } else if (errorLower.includes('expired')) {
        errorMessage = "Your card has expired. Please use a different card or update your card information with your bank.";
      }
      
      setMessage(errorMessage);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <section className="home-slide d-flex align-items-center">
      <div className="payment-page">
        <h2>Complete Your Payment {displayDiscountPercent > 0 ? (<h2 className="comission-rate">&nbsp;&nbsp;&nbsp;({displayDiscountPercent}% OFF)</h2>) : ""}</h2>
        <div className="discount">
          {previewLoading ? (
            <p className="total-amount">Calculating preview...</p>
          ) : previewError ? (
            <div>
              <p className="total-amount text-danger">⚠️ {previewError}</p>
              <p className="text-danger small mt-2">
                Payment preview is required. Please ensure the preview endpoint is available.
              </p>
            </div>
          ) : preview ? (
            <>
              {displayDiscountPercent > 0 ? (
                <>
                  <p className="total-amount">
                    Base Amount: <span className="has-discount">{originalAmountDisplay}</span>
                    <span> - {displayDiscountPercent}% = {lumpSumDisplay}</span>
                  </p>
                  {preview.promoCode && (
                    <p>Applied Promo Code: <span className="discount-code">{preview.promoCode}</span></p>
                  )}
                </>
              ) : (
                <p className="total-amount">Total Amount: {lumpSumDisplay}</p>
              )}
            </>
          ) : (
            <p className="total-amount">Total Amount: {lumpSumDisplay}</p>
          )}
        </div>

        <div className="plan-options">
          <p className="plan-heading">Choose your payment option:</p>
          <label className="plan-option">
            <input
              type="radio"
              name="planType"
              value="annual"
              checked={isAnnualPlan(planType)}
              onChange={(event) => handlePlanChange(event.target.value)}
            />
            <span>
              <strong>Pay annually</strong> — {lumpSumDisplay} today
              <br />
              Then {lumpSumDisplay} each year. Your card is securely saved and charged
              automatically at this rate — cancel anytime.
            </span>
          </label>
          <label className="plan-option">
            <input
              type="radio"
              name="planType"
              value="monthly"
              checked={isMonthlyPlan(planType)}
              onChange={(event) => handlePlanChange(event.target.value)}
            />
            <span>
              {previewLoading && isMonthlyPlan(planType) ? (
                <><strong>Pay monthly</strong> — calculating...</>
              ) : (
                <>
                  <strong>Pay monthly</strong> — {firstInstallmentDisplay} today
                  <br />
                  Then {recurringInstallmentDisplay} each month. Your card is securely saved
                  and charged automatically at this rate — cancel anytime.
                </>
              )}
            </span>
          </label>
          {/* One consent block for both plans. They were separate, which is how
              the monthly text came to promise "the remaining 11 installments"
              while the annual one promised renewal until cancelled. Only the
              amount and the cadence differ now; everything else is shared, so
              the two cannot drift apart again. */}
          {requiresCardPayment && (
            <>
              <p className="plan-note">
                Automatic renewal requires a credit or debit card. PayPal is not available for recurring billing.
              </p>
              <div className="recurring-consent">
                <input
                  type="checkbox"
                  checked={recurringConsent}
                  onChange={(event) => setRecurringConsent(event.target.checked)}
                />
                <div className="recurring-consent-text">
                  {isMonthlyPlan(planType) ? (
                    <>
                      I authorize AngelWatch to charge this card {firstInstallmentDisplay} today
                      and {recurringInstallmentDisplay} automatically every month to keep my
                      subscription active, until I cancel.
                    </>
                  ) : (
                    <>
                      I authorize AngelWatch to charge this card {lumpSumDisplay} today and
                      automatically every 12 months to keep my subscription active, until I cancel.
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Payment Method Selection */}
        <div className="payment-method">
          <button
            type="button"
            className={paymentMethod === "card" ? "active" : "nonactive"}
            onClick={() => {
              setPaymentMethod("card");
              setMessage("");
              setMessageType("error");
            }}
          >
            Credit/Debit
          </button>
        </div>

        {paymentMethod === "card" ? (
          <div className="card-details">
            <input
              type="text"
              name="cardNumber"
              placeholder="Card Number"
              value={form.cardNumber}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="expiry"
              placeholder="MM-YY"
              value={form.expiry}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="cardCode"
              placeholder="CVV"
              value={form.cardCode}
              onChange={handleChange}
              required
            />
            <div className="name-fields">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={form.firstName}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={form.lastName}
                onChange={handleChange}
                required
              />
            </div>
            <input
              type="text"
              name="address"
              placeholder="Billing Address"
              value={form.address}
              onChange={handleChange}
              required
            />
            <div className="name-fields">
              <input
                type="text"
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
                required
              />
            </div>
            <input
              type="text"
              name="zipCode"
              placeholder="Zip Code"
              value={form.zipCode}
              onChange={handleChange}
              required
            />
            <select
              name="country"
              value={form.country}
              onChange={handleChange}
              required
            >
              <option value="United States">United States</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Canada">Canada</option>
            </select>
            <input
              type="text"
              name="phone"
              placeholder="Phone Number (Optional)"
              value={form.phone}
              onChange={handleChange}
            />
            {!reemcode && (
              <div className="content-flex">
                <p className="content-text">Promo Code</p>
                <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                  <input
                    type="text"
                    className="content-input-text"
                    onChange={(e) => {
                      setReemCodeInput(e.target.value);
                      setPreviewError(null); // Clear error when user types
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyPromoCode();
                      }
                    }}
                    value={reemCodeInput}
                    placeholder="Enter promo code"
                    disabled={previewLoading}
                    style={{ width: '15ch', minWidth: '150px' }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleApplyPromoCode}
                    disabled={previewLoading || !reemCodeInput.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {previewLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {previewError && reemCodeInput && (
                  <p className="text-danger small mt-1 w-100">{previewError}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="paypal-details">
            <input
              type="email"
              name="payPalId" // Fixed: Correct field name
              placeholder="PayPal Email"
              value={form.payPalId}
              onChange={handleChange}
              required
            />
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading || (requiresCardPayment && !recurringConsent)}>
          {loading ? "Processing..." : "Pay Now"}
        </button>
        {message && (
          <p className={`validation-message ${messageType}`}>
            {message}
          </p>
        )}
      </div>
    </section>
  );
};

// ✅ Fixed PropTypes Definition
PaymentPage.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // First payment amount
  totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Total amount for pay in full
  installmentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Monthly installment amount
  reemcode: PropTypes.oneOfType([PropTypes.string]),
  onSuccess: PropTypes.func.isRequired, // Fixed: Should be a function
  pageType: PropTypes.oneOfType([PropTypes.string]).isRequired,
};

export default PaymentPage;
